/**
 * Prefab resolution (ADR-0002 tier b): a world's `prefabs` table maps NAMES to pure-data
 * extensions of framework classes — `{ extends, params }`. An entity placement whose `type` is a
 * prefab name resolves at load into a merged def against the base class. PURE module (no three,
 * no DOM, no zod) — shared by the runtime host, the editor's requires recomputation, the world
 * composer AND the Node emitter, so relative imports would need explicit `.ts` extensions (it
 * has none).
 *
 * PRECEDENCE (documented here and in ADR-0002):
 *   entity-level fields  >  prefab params  >  base-class schema defaults
 * For chains (prefab extends prefab), the placement's own fields still win, then the NEAREST
 * prefab's params, then each ancestor's in order, then schema defaults — i.e. every level
 * overrides everything beneath it.
 */

export interface WorldPrefab {
  extends: string;
  params: Record<string, unknown>;
}

export type PrefabTable = Record<string, WorldPrefab>;

/** Chain depth cap — deeper than this is a cycle (or absurd); resolution warns and yields null. */
export const PREFAB_DEPTH_CAP = 8;

/**
 * ⭐ P1.5·C — the `lib:` scheme, MIRRORED (from `src/assets/core/uri.ts`) rather than imported,
 * for the reason this module's header gives: it must load under `node --experimental-strip-types`
 * and `assets/core/uri.ts` reaches `./catalog` with no extension. `tests/assets.test.ts` already
 * holds `world/assets.ts`'s copy of this literal to the core's; this is the third and it is pinned
 * the same way.
 */
export const PREFAB_LIB_SCHEME = 'lib:';

/**
 * Resolve one entity placement through the prefab table.
 * - `type` not in the table → passthrough: `{ baseType: type, def }` unchanged.
 * - `type` is a prefab → merged def with `type` rewritten to the resolved base class name.
 * - malformed def (no object / no string `type`) or a chain past PREFAB_DEPTH_CAP → null (warned).
 * An `extends` target that is neither a prefab nor a registered class resolves to that name; the
 * caller's registry lookup / checkRequires flags it (this module stays registry-free on purpose).
 */
export function resolveEntityDef(
  def: unknown,
  prefabs: PrefabTable,
): { baseType: string; def: unknown } | null {
  if (typeof def !== 'object' || def === null || !('type' in def)) return null;
  const rec = def as Record<string, unknown>;
  if (typeof rec['type'] !== 'string') return null;
  let type: string = rec['type'];
  if (!(type in prefabs)) return { baseType: type, def }; // fast path: nothing to merge

  const { type: _placedType, ...merged } = rec;
  let acc: Record<string, unknown> = merged;
  let depth = 0;
  while (type in prefabs) {
    if (++depth > PREFAB_DEPTH_CAP) {
      console.warn(
        `[prefabs] '${String(rec['type'])}' exceeds the prefab chain depth cap ` +
          `(${PREFAB_DEPTH_CAP}) — cycle? Placement skipped.`,
      );
      return null;
    }
    const prefab = prefabs[type]!;
    // Never let a prefab's params smuggle a `type` — the chain alone decides the base class.
    const { type: _paramType, ...params } = prefab.params;
    acc = { ...params, ...acc }; // accumulated (nearer) levels win over this ancestor
    type = prefab.extends;
  }
  return { baseType: type, def: { ...acc, type } };
}

/**
 * Unique, sorted BASE framework class names a placement list requires (ADR-0001 `requires`),
 * resolved through the prefab table — prefab names are world-internal and never appear here.
 * Unresolvable placements (cycles, malformed) contribute nothing; unknown bases pass through so
 * checkRequires can flag them against the registry.
 */
export function computeRequires(entities: unknown[], prefabs: PrefabTable = {}): string[] {
  const names = new Set<string>();
  for (const e of entities) {
    const resolved = resolveEntityDef(e, prefabs);
    if (!resolved) continue;
    /*
     * ⭐ A `lib:` PLACEMENT CONTRIBUTES NOTHING HERE, AND THAT IS A STATEMENT ABOUT WHO OWNS THE
     * GATE (P1.5·C, plan §13.6). Its base class is named by the CATALOG, which this pure module
     * does not have and the world emitter cannot fetch, so the honest answer is silence — the
     * literal `lib:<id>` would be reported by `checkRequires` as an unregistered class on every
     * boot.
     *
     * ⭐ AND THE WORLD MUST NOT DECLARE IT BY HAND EITHER, WHICH IS THE STRONGER HALF. `requires`
     * BLOCKS A WHOLE WORLD; the capability gate for a library entity is the ASSET's own
     * `requires.classes` (folded from its `class` by `composeEntry` so it cannot go stale),
     * checked by `checkAssetRequires` at acquire and REFUSING BEFORE A BYTE MOVES. So a runtime
     * that lacks the class skips ONE PROP and loads the level, instead of refusing the level over
     * one decoration — and `requires === computeRequires` stays exactly true, which is the
     * invariant `tests/exploration/worlds.test.ts` holds every committed world to.
     *
     * That is also the coupling ADR-0003 exists to avoid: a catalog gains assets independently of
     * worlds (a season drop, a marketplace pack), and a world whose boot gate named a library
     * class would be a world that a catalog change could brick.
     */
    if (resolved.baseType.startsWith(PREFAB_LIB_SCHEME)) continue;
    names.add(resolved.baseType);
  }
  return [...names].sort();
}
